import {
  type BoxProps,
  FieldErrorText as EcFieldErrorText,
  FieldHelperText as EcFieldHelperText,
  FieldLabel as EcFieldLabel,
  FieldRequiredIndicator as EcFieldRequiredIndicator,
  FieldRoot as EcFieldRoot,
} from '@enechain/ecloud-designsystem'

// chakra v3.29 + React 18 types の既知不具合で FieldLabelProps / FieldErrorTextProps が
// { unstyled } のみに collapse し、children / style / htmlFor を JSX で受け取れなくなる。
// ecloud component をそのまま使い、取りこぼす props を補う型で 1 箇所に集約する。
const EcFieldLabelTyped = EcFieldLabel as React.FC<
  BoxProps & { htmlFor?: string; children?: React.ReactNode }
>

/**
 * FieldLabel の re-export。 chakra field slot recipe の default は
 * `textStyle: "sm"` + `fontWeight: "medium"` (= 12px/500) だが、
 * design SoT (`design/css/components.css` `.form-label`) は 14px/600/high-emphasis。
 * `~/constants/typography.ts` の `label` role (md/semibold) が同 SoT の canonical
 * 定義なのでここで default 適用する (chakra recipe が明示 override を持たないので
 * per-instance に textStyle="label" を撒く代わりに 1 箇所で集約)。
 * 呼び出し側で `textStyle` を渡せば個別 override 可。
 */
type FieldLabelProps = BoxProps & {
  htmlFor?: string
  children?: React.ReactNode
}
export const FieldLabel: React.FC<FieldLabelProps> = (
  props: FieldLabelProps,
) => {
  const { textStyle, color, children, ...rest } = props
  return (
    <EcFieldLabelTyped
      textStyle={textStyle ?? 'label'}
      color={color ?? 'Text.highEmphasis'}
      {...rest}
    >
      {children}
    </EcFieldLabelTyped>
  )
}
export const FieldErrorText = EcFieldErrorText as React.FC<BoxProps>

const EcFieldHelperTextTyped = EcFieldHelperText as React.FC<
  BoxProps & { children?: React.ReactNode }
>

/**
 * FieldHelperText の re-export。 chakra field slot recipe の default は **10px** だが、
 * éClair Figma の DS 定義は s (12px) で、 design SoT (`design/css/components.css`
 * `.form-hint`) も `--font-size-sm` (12px) / 400 / 1.4。 DS 実装が Figma 定義に
 * 追随できていないズレなので (2026-08-27 デザイナー確認)、
 * `~/constants/typography.ts` の `fieldHint` role をここで default 適用する
 * (FieldLabel と同じく per-instance に textStyle を撒く代わりに 1 箇所で集約)。
 * 呼び出し側で `textStyle` を渡せば個別 override 可。
 */
type FieldHelperTextProps = BoxProps & { children?: React.ReactNode }
export const FieldHelperText: React.FC<FieldHelperTextProps> = (
  props: FieldHelperTextProps,
) => {
  const { textStyle, children, ...rest } = props
  return (
    <EcFieldHelperTextTyped textStyle={textStyle ?? 'fieldHint'} {...rest}>
      {children}
    </EcFieldHelperTextTyped>
  )
}
export const FieldRoot = EcFieldRoot
export const FieldRequiredIndicator = EcFieldRequiredIndicator
